import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import './Guides.scss';

// Lista poradników - używana w sekcji „Zobacz też”, na stronie głównej i w stopce
export const GUIDES = [
    {
        to: '/co-to-nekrolog',
        title: 'Co to jest nekrolog i jak go napisać?',
        text: 'Co musi zawierać poprawny nekrolog i jak uniknąć błędów, przez które bliscy nie dotrą na pogrzeb.',
    },
    {
        to: '/klepsydra-wzor',
        title: 'Klepsydra - wzór do edycji i druku',
        text: 'Gotowy wzór tekstu klepsydry do skopiowania, lista obowiązkowych informacji i zasady wydruku.',
    },
    {
        to: '/napisy-na-szarfy',
        title: 'Napisy na szarfy pogrzebowe',
        text: 'Gotowe teksty na szarfy dla mamy, taty, babci, przyjaciela i od kolegów z pracy.',
    },
    {
        to: '/drukowanie-szarf-pogrzebowych',
        title: 'Drukowanie szarf pogrzebowych',
        text: 'Poradnik dla kwiaciarni: jak zrobić i wydrukować szarfę, wskazówki druku i program do szarf.',
    },
    {
        to: '/jak-zrobic-tabliczke-nagrobna',
        title: 'Jak zrobić tabliczkę nagrobną?',
        text: 'Sublimacja, druk UV, grawer czy folia - porównanie metod wykonania tabliczek na krzyż, urnę i trumnę.',
    },
    {
        to: '/marketing-zakladu-pogrzebowego',
        title: 'Marketing zakładu pogrzebowego',
        text: 'Wizytówka Google, strona internetowa, Facebook i opinie - jak rodziny znajdują zakład w internecie.',
    },
    {
        to: '/jakie-kwiaty-na-cmentarz',
        title: 'Jakie kwiaty na cmentarz?',
        text: 'Wieniec czy wiązanka, znaczenie lilii, róż i chryzantem oraz kwiaty na grób na każdą porę roku.',
    },
    {
        to: '/sprzedaz-zniczy-wszystkich-swietych',
        title: 'Sprzedaż zniczy na Wszystkich Świętych',
        text: 'Kalendarz przygotowań, asortyment, stoisko przy cmentarzu i dodatkowe usługi przed 1 listopada.',
    },
];

/**
 * Zdjęcie w treści poradnika z podpisem autora (zdjęcia z Unsplash, pobrane jako WebP 1200×700).
 * @param {{ photo: { src: string, alt: string, caption: string, author: string, source: string } }} props
 */
export function GuidePhoto({ photo }) {
    return (
        <figure className='guide_screenshot guide_photo'>
            <img src={photo.src} alt={photo.alt} width='1200' height='700' loading='lazy' />
            <figcaption>
                {photo.caption}. Fot.{' '}
                <a href={photo.source} target='_blank' rel='noopener noreferrer'>
                    {photo.author} / Unsplash
                </a>
            </figcaption>
        </figure>
    );
}

/**
 * Nawigacja na dole poradników: powrót na stronę główną i do listy poradników.
 * @param {{ showAllGuides?: boolean }} props - na stronie /poradniki ukrywamy link do niej samej
 */
export function GuideBackNav({ showAllGuides = true }) {
    return (
        <nav className='guide_backNav' aria-label='Nawigacja poradników'>
            <div className='guide_inner guide_backNavInner'>
                <Link to='/' className='guide_navBtn'>
                    ← Wróć do strony głównej
                </Link>
                {showAllGuides && (
                    <Link to='/poradniki' className='guide_navBtn guide_navBtn--outline'>
                        Wszystkie poradniki
                    </Link>
                )}
            </div>
        </nav>
    );
}

/**
 * Wspólny układ poradnika w stylu strony głównej.
 * @param {{ path: string, title: string, description: string, keywords: string, heading: React.ReactNode,
 *           lead: string, actions?: React.ReactNode, faq?: {question: string, answer: string}[],
 *           contact?: React.ReactNode, children: React.ReactNode }} props - `contact` trafia na sam dół, pod „Zobacz też”
 */
export default function GuideLayout({ path, title, description, keywords, heading, lead, actions, faq = [], contact = null, children }) {
    useEffect(() => {
        window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    }, [path]);

    const url = `https://nekrodruk.pl${path}`;
    const related = GUIDES.filter((guide) => guide.to !== path);

    return (
        <main className='guide'>
            <Helmet>
                <title>{title}</title>
                <meta name='description' content={description} />
                <meta name='keywords' content={keywords} />
                <meta name='robots' content='index, follow' />
                <meta property='og:title' content={title} />
                <meta property='og:description' content={description} />
                <meta property='og:type' content='article' />
                <meta property='og:url' content={url} />
                <link rel='canonical' href={url} />
                {faq.length > 0 && (
                    <script type='application/ld+json'>
                        {JSON.stringify({
                            '@context': 'https://schema.org',
                            '@type': 'FAQPage',
                            mainEntity: faq.map((item) => ({
                                '@type': 'Question',
                                name: item.question,
                                acceptedAnswer: { '@type': 'Answer', text: item.answer },
                            })),
                        })}
                    </script>
                )}
            </Helmet>

            <section className='guide_hero'>
                <div className='guide_inner'>
                    <nav className='guide_breadcrumbs' aria-label='Ścieżka'>
                        <Link to='/'>Strona główna</Link>
                        <span aria-hidden='true'>/</span>
                        <Link to='/poradniki'>Poradniki</Link>
                    </nav>
                    <span className='guide_kicker guide_kicker--light'>Poradnik</span>
                    <h1 className='guide_heroTitle'>{heading}</h1>
                    <p className='guide_heroLead'>{lead}</p>
                    {actions && <div className='guide_heroActions'>{actions}</div>}
                </div>
            </section>

            <div className='guide_body'>
                {children}

                {faq.length > 0 && (
                    <section className='guide_section'>
                        <div className='guide_inner'>
                            <h2 className='guide_title'>Najczęstsze pytania</h2>
                            <div className='guide_faq'>
                                {faq.map((item) => (
                                    <details key={item.question} className='guide_faqItem'>
                                        <summary>{item.question}</summary>
                                        <p>{item.answer}</p>
                                    </details>
                                ))}
                            </div>
                        </div>
                    </section>
                )}

                <section className='guide_section guide_section--soft'>
                    <div className='guide_inner'>
                        <h2 className='guide_title'>Zobacz też</h2>
                        <div className='guide_related'>
                            {related.map((guide) => (
                                <Link key={guide.to} to={guide.to} className='guide_relatedCard'>
                                    <span className='guide_kicker'>Poradnik</span>
                                    <strong>{guide.title}</strong>
                                    <span>{guide.text}</span>
                                </Link>
                            ))}
                        </div>
                    </div>
                </section>

                {contact}

                <GuideBackNav />
            </div>
        </main>
    );
}
